import React, { useRef } from 'react';
import { Building2, Landmark, Castle, Church, Warehouse, Home, Factory, Hotel, ImagePlus, Trash2 } from 'lucide-react';

// Imagem/ícone do projeto. Guardado inline em project.image como
// { type: 'icon', value: <keyword> } ou { type: 'upload', value: <data-url> }.
export type ProjectImage = { type: 'icon' | 'upload'; value: string } | null | undefined;

export const PROJECT_ICONS: { key: string; label: string; Icon: any }[] = [
  { key: 'building', label: 'Edifício', Icon: Building2 },
  { key: 'monument', label: 'Monumento', Icon: Landmark },
  { key: 'castle', label: 'Castelo', Icon: Castle },
  { key: 'church', label: 'Igreja', Icon: Church },
  { key: 'warehouse', label: 'Armazém', Icon: Warehouse },
  { key: 'house', label: 'Casa', Icon: Home },
  { key: 'factory', label: 'Fábrica', Icon: Factory },
  { key: 'hotel', label: 'Hotel', Icon: Hotel },
];

const ICON_MAP: { [k: string]: any } = PROJECT_ICONS.reduce((acc, i) => ({ ...acc, [i.key]: i.Icon }), {});

// Reduz a imagem no cliente (máx. 512px, JPEG) para manter o payload pequeno.
export const downscaleImage = (file: File, max = 512): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > max) { height = (height * max) / width; width = max; }
        else if (height >= width && height > max) { width = (width * max) / height; height = max; }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d')?.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Renderiza o visual dentro de uma caixa fornecida pelo pai (que define cor/arredondamento).
export const ProjectVisual: React.FC<{ image: ProjectImage; size?: number }> = ({ image, size = 28 }) => {
  if (image && image.type === 'upload' && image.value) {
    return <img src={image.value} alt="" className="w-full h-full object-cover" />;
  }
  const Icon = (image && image.type === 'icon' && ICON_MAP[image.value]) || Building2;
  return <Icon size={size} />;
};

// Seletor: grelha de ícones estruturais OU upload de uma imagem.
export const ProjectVisualPicker: React.FC<{ value: ProjectImage; onChange: (v: ProjectImage) => void; onError?: (m: string) => void }> = ({ value, onChange, onError }) => {
  const fileRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { onError?.('O ficheiro tem de ser uma imagem.'); return; }
    try {
      const dataUrl = await downscaleImage(file);
      onChange({ type: 'upload', value: dataUrl });
    } catch {
      onError?.('Não foi possível processar a imagem.');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
        {PROJECT_ICONS.map(({ key, label, Icon }) => {
          const active = value?.type === 'icon' && value.value === key;
          return (
            <button
              key={key}
              type="button"
              title={label}
              onClick={() => onChange({ type: 'icon', value: key })}
              className={`aspect-square rounded-2xl border-2 flex items-center justify-center transition-all ${active ? 'border-blue-600 bg-blue-600 text-white shadow-lg' : 'border-slate-100 dark:border-slate-800 text-slate-400 hover:border-blue-500/40'}`}
            >
              <Icon size={18} />
            </button>
          );
        })}
      </div>

      {value?.type === 'upload' && value.value ? (
        <div className="flex items-center justify-between gap-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3 min-w-0">
            <img src={value.value} alt="Projeto" className="w-14 h-14 rounded-xl object-cover border border-slate-200 dark:border-slate-700 shrink-0" />
            <span className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Imagem carregada</span>
          </div>
          <button type="button" onClick={() => onChange(null)} className="p-2.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors shrink-0">
            <Trash2 size={16} />
          </button>
        </div>
      ) : (
        <label className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700 cursor-pointer hover:border-blue-500/40 transition-all">
          <div className="p-2 bg-blue-600/10 text-blue-600 rounded-lg"><ImagePlus size={16} /></div>
          <span className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Ou carregar uma imagem</span>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
        </label>
      )}
    </div>
  );
};
